import type {
  FormDraftConfig,
  FormField,
  FormSalesforceDestination,
  FormSalesforceDestinationType,
  FormSalesforceEvidenceExport,
  FormSalesforceFieldExport,
} from '@/modules/forms/formConfig';
import { SALESFORCE_DESTINATION_TYPES } from '@/modules/forms/formConfig';

import type { SalesforceFieldDescription } from './metadata';

export interface SalesforceFormMapping {
  objectAPIName: string;
  householdLookup: { fieldAPIName: string };
  mappedFields: SalesforceMappedField[];
}

export interface SalesforceMappedField extends FormSalesforceFieldExport {
  field: FormField;
}

export const SALESFORCE_EVIDENCE_EXPORT_OPTIONS = [
  { value: 'includeReasoning', display: 'Include reasoning summary' },
  { value: 'includeQuotes', display: 'Include quotes' },
  { value: 'includeCitations', display: 'Include citations' },
] as const;

export type SalesforceDestinationField = SalesforceFieldDescription & {
  type: FormSalesforceDestinationType;
};

export function isSalesforceDestinationField(
  field: SalesforceFieldDescription
): field is SalesforceDestinationField {
  return SALESFORCE_DESTINATION_TYPES.some((type) => type === field.type);
}

/** Read the canonical field-owned mapping without creating a parallel model. */
export function readSalesforceMapping(
  config: FormDraftConfig | undefined
): SalesforceFormMapping | undefined {
  const salesforce = config?.exports?.salesforce;
  if (!salesforce) return undefined;
  const mappedFields: SalesforceMappedField[] = [];
  const visit = (fields: FormField[]) => {
    for (const field of fields) {
      if (field.exports?.salesforce)
        mappedFields.push({ field, ...field.exports.salesforce });
      visit(field.children ?? []);
    }
  };
  for (const section of config.form) visit(section.children);
  return { ...salesforce, mappedFields };
}

export function destinationSnapshot(
  destination: SalesforceDestinationField
): FormSalesforceDestination {
  return {
    type: destination.type,
    label: destination.label,
    helpText: destination.helpText,
    required: !destination.nillable && !destination.defaultedOnCreate,
    length: destination.length > 0 ? destination.length : null,
    picklistValues: destination.picklistValues
      .filter((value) => value.active)
      .map((value) => value.value),
  };
}

function updateField(
  config: FormDraftConfig,
  fieldID: string,
  update: (field: FormField) => FormField
): FormDraftConfig {
  const visit = (fields: FormField[]): FormField[] =>
    fields.map((field) => {
      const current = field.id === fieldID ? update(field) : field;
      return current.children
        ? { ...current, children: visit(current.children) }
        : current;
    });
  return {
    ...config,
    form: config.form.map((section) => ({
      ...section,
      children: visit(section.children),
    })),
  };
}

export function setSalesforceAnswerDestination(
  config: FormDraftConfig,
  fieldID: string,
  destination: SalesforceDestinationField,
  sentAs: FormSalesforceFieldExport['sentAs'] = []
): FormDraftConfig {
  return updateField(config, fieldID, (field) => ({
    ...field,
    exports: {
      salesforce: {
        fieldAPIName: destination.apiName,
        destination: destinationSnapshot(destination),
        sentAs,
        evidence: field.exports?.salesforce.evidence,
      },
    },
  }));
}

export function setSalesforceEvidenceDestination(
  config: FormDraftConfig,
  fieldID: string,
  evidence: FormSalesforceEvidenceExport | undefined
): FormDraftConfig {
  return updateField(config, fieldID, (field) => {
    const mapping = field.exports?.salesforce;
    if (!mapping) return field;
    return {
      ...field,
      exports: { salesforce: { ...mapping, evidence } },
    };
  });
}

export function removeSalesforceDestination(
  config: FormDraftConfig,
  fieldAPIName: string
): FormDraftConfig {
  const visit = (fields: FormField[]): FormField[] =>
    fields.map((field) => {
      const mapping = field.exports?.salesforce;
      let next = field;
      if (mapping?.fieldAPIName === fieldAPIName) {
        next = { ...field, exports: undefined };
      } else if (mapping) {
        next = {
          ...field,
          exports: {
            salesforce: {
              ...mapping,
              evidence:
                mapping.evidence?.fieldAPIName === fieldAPIName
                  ? undefined
                  : mapping.evidence,
            },
          },
        };
      }
      return next.children ? { ...next, children: visit(next.children) } : next;
    });
  return {
    ...config,
    form: config.form.map((section) => ({
      ...section,
      children: visit(section.children),
    })),
  };
}

export function destinationOwner(
  mapping: SalesforceFormMapping,
  apiName: string
): SalesforceMappedField | undefined {
  return mapping.mappedFields.find(
    (field) =>
      field.fieldAPIName === apiName || field.evidence?.fieldAPIName === apiName
  );
}

export function answerDestinationCandidates(
  mapping: SalesforceFormMapping,
  field: FormField,
  destinations: SalesforceFieldDescription[]
): SalesforceDestinationField[] {
  const type = answerDestinationType(field);
  if (!type) return [];
  const current = field.exports?.salesforce.fieldAPIName;
  return destinations.filter(
    (destination): destination is SalesforceDestinationField =>
      isSalesforceDestinationField(destination) &&
      destination.type === type &&
      isWritable(destination) &&
      destination.apiName !== mapping.householdLookup.fieldAPIName &&
      (destination.apiName === current ||
        !destinationOwner(mapping, destination.apiName))
  );
}

export function evidenceDestinationCandidates(
  mapping: SalesforceFormMapping,
  currentAPIName: string | undefined,
  destinations: SalesforceFieldDescription[]
): (SalesforceDestinationField & {
  type: FormSalesforceEvidenceExport['type'];
})[] {
  return destinations.filter(
    (
      destination
    ): destination is SalesforceDestinationField & {
      type: FormSalesforceEvidenceExport['type'];
    } =>
      isSalesforceDestinationField(destination) &&
      (destination.type === 'string' || destination.type === 'textarea') &&
      isWritable(destination) &&
      destination.apiName !== mapping.householdLookup.fieldAPIName &&
      (destination.apiName === currentAPIName ||
        !destinationOwner(mapping, destination.apiName))
  );
}

function answerDestinationType(
  field: FormField
): SalesforceDestinationField['type'] | undefined {
  switch (field.fieldType) {
    case 'shortText':
      return 'string';
    case 'longText':
      return 'textarea';
    case 'date':
      return 'date';
    case 'singleSelect':
      return 'picklist';
    default:
      return undefined;
  }
}

function isWritable(field: SalesforceFieldDescription): boolean {
  return field.createable && !field.calculated && !field.autoNumber;
}
